import { z } from 'zod';
/**
 * Timeframes the system knows about.
 *
 * `source` records where a series comes from. Only M1..H1 are pulled from the
 * broker; H4 and above are aggregated from H1, because the exporter applies a
 * fixed +3h shift to a UTC broker feed and a shift only preserves bar alignment
 * when it divides the bar duration exactly (10800 % 14400 != 0, so a broker H4
 * would sit on a 03:00/07:00/11:00 boundary instead of 00:00/04:00/08:00).
 */
export const TIMEFRAMES = [
    'M1',
    'M5',
    'M15',
    'M30',
    'H1',
    'H4',
    'H4_1',
    'H4_2',
    'H4_3',
    'D1',
    'W1',
];
/**
 * The four H4 alignments.
 *
 * A four-hour grid repeats every four hours, so every broker's H4 — whether its
 * candle opens at 12:00, 14:00, 15:00 or 17:00 on that broker's own clock —
 * reduces to one of exactly four phases. These are all of them: `H4` sits on our
 * session anchor and each variant is offset a further hour, so between them they
 * reproduce any broker's H4 without needing to know its timezone.
 *
 * Offsets are relative to OUR anchor rather than to a fixed UTC hour, so all
 * four move together across a DST change instead of three of them drifting away
 * from the one the session rules define.
 */
export const H4_VARIANTS = ['H4', 'H4_1', 'H4_2', 'H4_3'];
/** Hours each H4 variant opens after the session-anchored one. */
export const H4_OFFSET_HOURS = {
    H4: 0,
    H4_1: 1,
    H4_2: 2,
    H4_3: 3,
};
export const timeframeSchema = z.enum(TIMEFRAMES);
/** Bar duration in seconds. W1 is nominal — real weeks are cut at the session open. */
export const TIMEFRAME_SECONDS = {
    M1: 60,
    M5: 300,
    M15: 900,
    M30: 1800,
    H1: 3600,
    H4: 14400,
    H4_1: 14400,
    H4_2: 14400,
    H4_3: 14400,
    D1: 86400,
    W1: 604800,
};
export const TIMEFRAME_LABEL = {
    M1: '1 min',
    M5: '5 min',
    M15: '15 min',
    M30: '30 min',
    H1: '1 hour',
    H4: '4 hour',
    H4_1: '4 hour (+1h)',
    H4_2: '4 hour (+2h)',
    H4_3: '4 hour (+3h)',
    D1: 'Daily',
    W1: 'Weekly',
};
/** Written by the MT5 exporter and ingested verbatim. */
export const BROKER_TIMEFRAMES = ['M1', 'M5', 'M15', 'H1'];
/** Built by the aggregator rather than fetched from the broker. */
export const DERIVED_TIMEFRAMES = [
    'M30',
    'H4',
    'H4_1',
    'H4_2',
    'H4_3',
    'D1',
    'W1',
];
/**
 * Which timeframe each derived one is aggregated FROM.
 *
 * M30 comes from M15, not H1: H1 is coarser than the target, so it carries no
 * information about where the half-hour boundary falls. Everything above H1
 * still derives from H1, which keeps a single source of truth for the session
 * boundaries.
 */
export const DERIVED_SOURCE = {
    M30: 'M15',
    H4: 'H1',
    H4_1: 'H1',
    H4_2: 'H1',
    H4_3: 'H1',
    D1: 'H1',
    W1: 'H1',
};
export function isTimeframe(value) {
    return TIMEFRAMES.includes(value);
}
export function isDerived(tf) {
    return DERIVED_TIMEFRAMES.includes(tf);
}
/**
 * Short label for a timeframe button.
 *
 * The storage keys carry an underscore so they fit the varchar(4) column, which
 * is not what anyone wants to read on a chart toolbar.
 */
export const TIMEFRAME_SHORT = {
    H4_1: 'H4+1',
    H4_2: 'H4+2',
    H4_3: 'H4+3',
};
export function timeframeShort(tf) {
    return TIMEFRAME_SHORT[tf] ?? tf;
}
/**
 * The finer timeframe each one is refined against.
 *
 * A pivot found on H1 is only located to the nearest hour, and its low is
 * whatever the lowest tick in that hour happened to be. Looking at the child
 * timeframe gives several nearby extremes to choose between, which is what lets
 * a swing be anchored to the price that actually fits the reactions rather than
 * to an arbitrary one.
 */
export const CHILD_TIMEFRAME = {
    M1: null,
    M5: 'M1',
    M15: 'M5',
    M30: 'M15',
    H1: 'M15',
    H4: 'H1',
    H4_1: 'H1',
    H4_2: 'H1',
    H4_3: 'H1',
    D1: 'H1',
    W1: 'D1',
};
//# sourceMappingURL=timeframe.js.map